// ─── Pure helper functions extracted from ShipmentDetailPage ─────────────────

// ─── deliveryNoteStatus helpers ──────────────────────────────────────────────

export type DeliveryNoteStatus = "normal" | "validado" | "reclamado"

/** Display label for each deliveryNoteStatus value. */
export const DELIVERY_NOTE_STATUS_LABEL: Record<DeliveryNoteStatus, string> = {
  normal:    "sin validar",
  validado:  "validado",
  reclamado: "reclamado",
}

/** Tailwind classes for each deliveryNoteStatus value. */
export const DELIVERY_NOTE_STATUS_CLASS: Record<DeliveryNoteStatus, string> = {
  normal:    "bg-muted text-muted-foreground",
  validado:  "bg-emerald-100 text-emerald-700",
  reclamado: "bg-red-100 text-red-700",
}

/** Hover classes for each deliveryNoteStatus value (for clickable badges). */
export const DELIVERY_NOTE_STATUS_HOVER_CLASS: Record<DeliveryNoteStatus, string> = {
  normal:    "hover:bg-muted/70",
  validado:  "hover:bg-emerald-200",
  reclamado: "hover:bg-red-200",
}

/** Cyclic transition: normal → validado → reclamado → normal. */
export const DELIVERY_NOTE_STATUS_NEXT: Record<DeliveryNoteStatus, DeliveryNoteStatus> = {
  normal:    "validado",
  validado:  "reclamado",
  reclamado: "normal",
}


export function formatRoutePath(
  origin: string | null | undefined,
  routeName: string | null | undefined,
  fallbackDestination: string | null | undefined,
  intermediateStops: string | null | undefined = null,
): string {
  const stops = intermediateStops
    ?.split("||")
    .map(stop => stop.trim())
    .filter(Boolean) ?? []
  if (stops.length > 0) {
    return [origin, ...stops, fallbackDestination]
      .filter((part): part is string => Boolean(part))
      .join(" / ")
  }

  const routeSegments = routeName
    ?.split("/")
    .map(stop => stop.trim())
    .filter(Boolean) ?? []
  return [origin, ...(routeSegments.length > 0 ? routeSegments : [fallbackDestination])]
    .filter((part): part is string => Boolean(part))
    .join(" / ")
}

export function fmtCargoValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—"
  return `ARS ${new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value))}`
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleString("es-AR", {
    day:    "2-digit",
    month:  "long",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  })
}

export function isoToLocalDatetime(iso: string | null | undefined): string {
  if (!iso) return ""
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}


export function fmtTripPlanDate(iso: string | undefined): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("es-AR", {
    weekday: "long",
    day:     "numeric",
    month:   "long",
    year:    "numeric",
    hour:    "2-digit",
    minute:  "2-digit",
  })
}

export function fmtTripPlanShort(iso: string | undefined): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("es-AR", {
    day:    "numeric",
    month:  "short",
    hour:   "2-digit",
    minute: "2-digit",
  })
}
